import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "../lib/utils";
import { useIsMobile } from "../hooks/use-is-mobile";
import MobileSearchableSelect from "./mobile/mobile-searchable-select";

interface Option {
  value: string;
  label: string;
  icon?: string;
  disabled?: boolean;
}

interface SearchableSelectInputProps {
  options: Option[];
  onSelect: (value: string) => void;
  value?: string;
  defaultLabel?: string;
  className?: string;
}

const SearchableSelectInput: React.FC<SearchableSelectInputProps> = (props) => {
  const isMobile = useIsMobile();
  if (isMobile) return <MobileSearchableSelect {...props} />;
  return <DesktopSearchableSelectInput {...props} />;
};

const DesktopSearchableSelectInput: React.FC<SearchableSelectInputProps> = ({
  options,
  onSelect,
  value,
  defaultLabel,
  className,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filteredOptions: Option[] = options?.filter((option) =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const maxHeight = Math.min(288, window.innerHeight - rect.bottom - 16);
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(maxHeight, 120),
      zIndex: 10000,
    });
  };

  const toggleDropdown = (): void => {
    setIsOpen((open) => !open);
  };

  useEffect(() => {
    if (!isOpen) return;

    updateDropdownPosition();

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleReposition = () => updateDropdownPosition();

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) setSearchTerm("");
  }, [isOpen]);

  const selectedLabel = value
    ? options.find((item) => item.value === value)?.label
    : undefined;

  const dropdown = isOpen ? (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
      className="rounded-lg bg-white shadow-lg border border-gray-200 flex flex-col overflow-hidden"
    >
      <input
        type="text"
        value={searchTerm}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setSearchTerm(e.target.value)
        }
        className="w-full border-b border-gray-200 p-2 outline-none focus:ring-0 flex-shrink-0"
        placeholder="Search..."
        autoFocus
      />
      <ul className="overflow-y-auto flex-1 min-h-0 p-1">
        {value && (
          <li
            className="cursor-pointer p-2 text-left hover:bg-gray-100 flex items-center text-gray-600 border-b border-gray-100 rounded-sm"
            onClick={() => {
              onSelect("");
              setIsOpen(false);
            }}
          >
            <span className="text-xs">Clear selection</span>
          </li>
        )}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <li
              key={option.value}
              className={cn(
                "cursor-pointer p-2 text-left hover:bg-gray-100 flex items-center rounded-sm",
                {
                  "text-gray-300 cursor-not-allowed": option.disabled,
                  "text-gray-900": !option.disabled,
                  "bg-gray-100 text-gray-900": value === option.value,
                },
              )}
              onClick={() => {
                if (!option.disabled) {
                  onSelect(option.value);
                  setIsOpen(false);
                }
              }}
            >
              {option.icon && <span className={`bi bi-${option.icon} mr-2`} />}
              <span className="break-words">{option.label}</span>
            </li>
          ))
        ) : (
          <li className="p-2 text-gray-500">No results found</li>
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={toggleDropdown}
        className="flex w-full min-h-9 items-center justify-between gap-2 rounded-md bg-white px-2 py-2 text-left text-sm text-gray-700 border border-input hover:bg-gray-50"
      >
        <span className={cn("truncate", !selectedLabel && "text-gray-400")}>
          {selectedLabel ?? defaultLabel ?? "Select an option"}
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" />
      </button>

      {typeof document !== "undefined" && dropdown
        ? createPortal(dropdown, document.body)
        : null}
    </div>
  );
};

export default SearchableSelectInput;
